import { Button, TextInput } from "@/components"
import { PlaceholderButton } from "@/components/PlaceholderButton/PlaceholderButton"
import { useStyles } from "@/components/InputWithPlaceholders/InputWithPlaceholders.styles"
import { placeholders as constantPlaceholders } from "app/static/frontend/campaign/campaign-constants"
import React, { useMemo, useRef } from "react"
import { FieldValues, RegisterOptions, useFormContext } from "react-hook-form"

interface Props<T extends keyof React.JSX.IntrinsicElements | React.JSXElementConstructor<any>> {
    //The component to be used for the input. Defaults to TextInput
    //usage: renderInput={(props) => <Textarea myTextAreaProp={myValue} {...props} />} or simply renderInput={TextArea}
    renderInput?: (props: React.ComponentProps<T>) => JSX.Element
    name: string
    label?: string
    placeholder?: string
    rules: Omit<RegisterOptions<FieldValues, string>, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">
    placeholders?: {
        slug: string
        value: string
        name: string
    }[]
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const InputWithPlaceholders = ({
    placeholders,
    renderInput = TextInput,
    ...props
}: Props<typeof renderInput>) => {
    const { classes } = useStyles()
    const { watch, setValue } = useFormContext()
    const trackedSelection = useRef<{ start: number; end: number }>({ start: 0, end: 0 })

    const handleOnSelect = (event: React.SyntheticEvent) => {
        const { selectionStart, selectionEnd } = event.target as HTMLInputElement | HTMLTextAreaElement
        trackedSelection.current = { start: selectionStart, end: selectionEnd }
    }

    const value = watch(props.name)

    const defaultPlaceholders = React.useMemo(() => constantPlaceholders.placeholdersSupportersOnly[0].items ?? [], [])

    const insertPlaceholder = (placeholder: string) => {
        const textBeforeCursor = value.slice(0, trackedSelection.current.start)
        const textAfterCursor = value.slice(trackedSelection.current.end)
        const newValue = `${textBeforeCursor}${placeholder}${textAfterCursor}`
        setValue(props.name, newValue)
    }

    const inputProps = useMemo(() => ({ ...props, triggerOnChange: false }), [props])

    return (
        <div className={classes.container}>
            {renderInput({ ...inputProps, onSelect: handleOnSelect })}
            <PlaceholderButton
                Wrapper={(props) => <Button variant="outline" color="gray" size="xs" {...props} />}
                onClick={(placeholder: string) => insertPlaceholder(placeholder)}
                placeholders={placeholders ?? defaultPlaceholders}
            />
        </div>
    )
}
